import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Divider,
  Dropdown,
  Flex,
  Layout,
  List,
  MenuProps,
  Tabs,
  TabsProps,
  Typography,
} from "antd";
import { BsFilterLeft } from "react-icons/bs";
import SearchInput from "../../../components/common/UI/SearchInput";
import CallCard from "../../../components/calls/CallCard";
import CustomLoader from "../../../components/common/UI/Loader";
import "./index.scss";
import { useGetCallLogsQuery } from "../../../redux/services/api";

const { Sider } = Layout;
const { Title } = Typography;
const FilterIcon = BsFilterLeft as unknown as React.FC;

const searchItems: MenuProps["items"] = [
  { key: "all", label: "All" },
  { key: "efone", label: "Efone" },
  { key: "non-efone", label: "Non Efone" },
];

interface CallsSidebarProps {
  onCallClick: React.Dispatch<React.SetStateAction<any>>;
}

const CallsSidebar: React.FC<CallsSidebarProps> = ({ onCallClick }) => {
  const { 
    data: recentCallsData, 
    isLoading: recentCallsDataLoading,
    isFetching: recentCallsDataFetching
  } = useGetCallLogsQuery();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [callsFilterValue, setCallsFilterValue] = useState("all");

  // Debounce effect: updates debouncedSearchTerm after 300ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Use useMemo to derive filtered data directly from recentCallsData
  // This avoids the "No Data" flicker because it's calculated during render
  const { filteredCallsData, filteredMissedCallsData } = useMemo(() => {
    if (!recentCallsData?.data) {
      return { filteredCallsData: [], filteredMissedCallsData: [] };
    }

    let filteredAll: any = [...recentCallsData?.data];
    let filteredMissed = recentCallsData?.data.filter(
      (item: any) => item.callStatus === false,
    );

    // Apply type filter
    if (callsFilterValue === "efone") {
      filteredAll = filteredAll.filter((item: any) => item.iseFone2eFone);
      filteredMissed = filteredMissed.filter((item: any) => item.iseFone2eFone);
    } else if (callsFilterValue === "non-efone") {
      filteredAll = filteredAll.filter((item: any) => !item.iseFone2eFone);
      filteredMissed = filteredMissed.filter(
        (item: any) => !item.iseFone2eFone,
      );
    }

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase();
      const matchesSearch = (item: any) =>
        item.fullName?.toLowerCase().includes(term) ||
        item.sourceNumber?.toLowerCase().includes(term) ||
        item.destinationNumber?.toLowerCase().includes(term);

      filteredAll = filteredAll.filter(matchesSearch);
      filteredMissed = filteredMissed.filter(matchesSearch);
    }

    return { 
      filteredCallsData: filteredAll, 
      filteredMissedCallsData: filteredMissed 
    };
  }, [recentCallsData, callsFilterValue, debouncedSearchTerm]);

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "All",
      children: (
        <Flex vertical gap={14} className="w-full h-full">
          <Flex gap={10}>
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search..."
            />
            <Dropdown
              overlayClassName="custom-dropdown"
              trigger={["click"]}
              menu={{
                items: searchItems,
                onClick: (e) => setCallsFilterValue(e.key),
              }}
            >
              <Button type="link" className="filter-btn">
                <FilterIcon />
              </Button>
            </Dropdown>
          </Flex>

          <List
            className="recent-calls-list"
            dataSource={filteredCallsData}
            loading={{
              spinning: recentCallsDataLoading || (recentCallsDataFetching && filteredCallsData.length === 0),
              indicator: <CustomLoader />,
            }}
            renderItem={(item: any) => (
              <div key={item._id}>
                <CallCard data={item} onCallClick={onCallClick} />
                <Divider className="divider" />
              </div>
            )}
          />
        </Flex>
      ),
    },
    {
      key: "2",
      label: "Missed",
      children: (
        <Flex vertical gap={10} className="w-full h-full">
          <Flex gap={10}>
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search..."
            />
            <Dropdown
              overlayClassName="custom-dropdown"
              trigger={["click"]}
              menu={{
                items: searchItems,
                onClick: (e) => setCallsFilterValue(e.key),
              }}
            >
              <Button type="link" className="filter-btn">
                <FilterIcon />
              </Button>
            </Dropdown>
          </Flex>
          <List
            className="recent-calls-list"
            dataSource={filteredMissedCallsData}
            loading={{
              spinning: recentCallsDataLoading || (recentCallsDataFetching && filteredMissedCallsData.length === 0),
              indicator: <CustomLoader />,
            }}
            renderItem={(item: any) => (
              <div key={item._id}>
                <CallCard data={item} onCallClick={onCallClick} />
                <Divider className="divider" />
              </div>
            )}
          />
        </Flex>
      ),
    },
  ];

  return (
    <Sider className="calls-sidebar">
      <Title className="main-title">Calls</Title>
      <Tabs
        defaultActiveKey="1"
        items={items}
        className="call-tabs"
      />
    </Sider>
  );
};

export default CallsSidebar;
